
import {
  CreateMediaSFURoomOptions,
  JoinMediaSFURoomOptions
} from '../../@types/types';

export type CreateJoinRoomType = (options: {
  payload: CreateMediaSFURoomOptions | JoinMediaSFURoomOptions;
  apiUserName: string;
  apiKey: string;
  localLink?: string;
  /**
   * Full MediaSFU Cloud rooms endpoint to use instead of the default
   * production one, e.g. 'https://mediasfu.com/v1/rooms/'.
   *
   * Separate from `localLink`, which is the Community Edition server link and
   * carries entirely different semantics.
   */
  roomsEndpoint?: string;
}) => Promise<{
  data: CreateJoinRoomResponse | CreateJoinRoomError | null;
  success: boolean;
}>;

export type CreateRoomOnMediaSFUType = (options: {
  payload: CreateMediaSFURoomOptions;
  apiUserName: string;
  apiKey: string;
  localLink?: string;
  /**
   * Full MediaSFU Cloud rooms endpoint to use instead of the default
   * production one, e.g. 'https://mediasfu.com/v1/rooms/'.
   *
   * Separate from `localLink`, which is the Community Edition server link and
   * carries entirely different semantics.
   */
  roomsEndpoint?: string;
}) => Promise<{
  data: CreateJoinRoomResponse | CreateJoinRoomError | null;
  success: boolean;
}>;


export interface CreateJoinRoomResponse {
  message: string;
  roomName: string;
  secureCode?: string;
  publicURL: string;
  link: string;
  secret: string;
  success: boolean;
}

export interface CreateJoinRoomError {
  error: string;
  success?: boolean;
}

const readRoomApiResponse = async (
  response: Response
): Promise<CreateJoinRoomResponse | CreateJoinRoomError> => {
  const responseText = await response.text();
  let body: any = null;
  if (responseText) {
    try {
      body = JSON.parse(responseText);
    } catch {
      body = null;
    }
  }

  if (!response.ok) {
    const backendReason =
      (typeof body?.error === "string" && body.error.trim()) ||
      (typeof body?.reason === "string" && body.reason.trim()) ||
      (typeof body?.message === "string" && body.message.trim());
    return {
      error: backendReason || `Room request failed with HTTP ${response.status}.`,
      success: false,
    };
  }

  if (!body || typeof body !== "object") {
    return {
      error: "Room service returned an invalid response.",
      success: false,
    };
  }

  if (body.success === false || typeof body.error === "string") {
    return {
      error: String(body.error || body.reason || body.message || "Room request failed."),
      success: false,
    };
  }

  return body as CreateJoinRoomResponse;
};

export type JoinRoomOnMediaSFUType = (options: {
  payload: JoinMediaSFURoomOptions;
  apiUserName: string;
  apiKey: string;
  localLink?: string;
  /**
   * Full MediaSFU Cloud rooms endpoint to use instead of the default
   * production one, e.g. 'https://mediasfu.com/v1/rooms/'.
   *
   * Separate from `localLink`, which is the Community Edition server link and
   * carries entirely different semantics.
   */
  roomsEndpoint?: string;
}) => Promise<{
  data: CreateJoinRoomResponse | CreateJoinRoomError | null;
  success: boolean;
}>;

/**
 * Asynchronously joins a room on MediaSFU.
 *
 * This function sends a POST request to the MediaSFU API to join an existing room. 
 * It validates the provided credentials and constructs the appropriate API endpoint, 
 * including support for the Community Edition via a custom `localLink`.
 *
 * @param {object} options - Configuration options for joining the room.
 * @param {JoinMediaSFURoomOptions | CreateMediaSFURoomOptions} options.payload - The payload containing the room details and action (`join` or `create`).
 * @param {string} options.apiUserName - The API username, used for authentication.
 * @param {string} options.apiKey - The API key, used for authentication.
 * @param {string} [options.localLink=""] - The local link for Community Edition users. If provided, it overrides the default API URL.
 * 
 * @returns {Promise<{ 
*   data: CreateJoinRoomResponse | CreateJoinRoomError | null; 
*   success: boolean; 
* }>} A promise resolving to an object containing the API response:
* - `data`: The response object, either `CreateJoinRoomResponse` or `CreateJoinRoomError`.
* - `success`: Boolean indicating whether the operation was successful.
*
* @throws {Error} Throws an error if the request fails or if the provided credentials are invalid.
*
* @example
* ```ts
* const response = await joinRoomOnMediaSFU({
*   payload: {
*     action: 'join',
*     meetingID: '123456',
*     userName: 'user123',
*   },
*   apiUserName: 'yourAPIUSERNAME',
*   apiKey: 'yourAPIKEY',
*   localLink: 'http://localhost:3000', // Optional for Community Edition
* });
*
* if (response.success) {
*   console.log('Joined room:', response.data);
* } else {
*   console.error('Failed to join room:', response.data?.error);
* }
* ```
*/


export const joinRoomOnMediaSFU: CreateJoinRoomType = async ({
  payload,
  apiUserName,
  apiKey,
  localLink,
    roomsEndpoint = "",
}: {
  payload: JoinMediaSFURoomOptions | CreateMediaSFURoomOptions;
  apiUserName: string;
  apiKey: string;
  localLink?: string;
  roomsEndpoint?: string;
}): Promise<{
  data: CreateJoinRoomResponse | CreateJoinRoomError | null;
  success: boolean;
}> => {
  try {
    if (
      !apiUserName ||
      !apiKey ||
      apiUserName === "yourAPIUSERNAME" ||
      apiKey === "yourAPIKEY" ||
      apiKey.length !== 64 ||
      apiUserName.length < 6
    ) {
      return { data: { error: "Invalid credentials" }, success: false };
    }

    let finalLink = 'https://mediasfu.com/v1/rooms/';
    if (roomsEndpoint && roomsEndpoint.trim() !== '') {
      // An explicit Cloud endpoint (staging, or a backend proxy that mirrors
      // the same contract) is used verbatim.
      finalLink = roomsEndpoint;
    }
    if ( localLink && localLink.trim() !== ''  && !localLink.includes('mediasfu.com') ){
      localLink = localLink.replace(/\/$/, '');
      finalLink = localLink + '/joinRoom';
    }

    const response = await fetch(finalLink,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiUserName}:${apiKey}`,
        },
        body: JSON.stringify(payload),
      }
    );

    const data = await readRoomApiResponse(response);
    if ("error" in data) {
      return { data, success: false };
    }
    return { data, success: true };
  } catch (error) {
    const errorMessage = (
      error as { reason?: string; message?: string }
    ).reason || (
      error as { reason?: string; message?: string }
    ).message || 'unknown error';
    return {
      data: { error: `Unable to join room, ${errorMessage}` },
      success: false,
    };
  }
}
